import { mapState } from "vuex";
import { ref, onMounted, watch } from "vue";
import * as R from "ramda";
import { Line } from "vue-chartjs";
import ChartsMixin from "@/mixins/ChartsMixin.vue";
import { CurrencyValue, PercentageValue, ValueElement } from "@/components/tableElements";
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend } from "chart.js";
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);
import AppPopover from "@/components/AppPopover";
export default {
  name: "RoasGraph",
  components: {
    Line,
    AppPopover,
    CurrencyValue,
    PercentageValue,
    ValueElement
  },
  mixins: [ChartsMixin],
  props: {
    values: {
      type: Array,
      required: true
    },
    selected_period: {
      type: String,
      default: "14 days"
    },
    workspace_id: {
      type: String,
      default: ""
    },
    seller_id: {
      type: String,
      default: ""
    }
  },
  data() {
    return {
      height: 45,
      grayColor: "#BFBFBF",
      greenColor: "#22863D",
      redColor: "#DB333B",
      lineColor: "#C9CFD4",
      pointPositions: [],
      periodRange: {
        "14 days": 1,
        "12 weeks": 7,
        "12 months": 30,
        "12 quarters": 90
      },
      hovering_index: -1
    };
  },
  computed: {
    ...mapState({}),
    uid() {
      return crypto.randomUUID();
    },
    isHovering() {
      return index => {
        return this.hovering_index === index ? "hovering" : "";
      };
    },
    processedData() {
      const allZero = this.values.every(v => Number(v.ROAS) === 0);
      return {
        labels: this.values.map(() => ""),
        datasets: [{
          label: "",
          clip: false,
          borderColor: allZero ? "#BFBFBF" : this.lineColor,
          backgroundColor: this.lineColor,
          borderWidth: 1.5,
          data: this.values.map(v => Number(v?.ROAS)),
          pointRadius: ctx => {
            const index = ctx.dataIndex;
            const lastIndex = ctx.dataset.data.length - 1;
            return index === lastIndex ? 5 : 0;
          },
          pointBackgroundColor: ctx => {
            const index = ctx.dataIndex;
            const lastIndex = ctx.dataset.data.length - 1;
            return index === lastIndex ? "#F4F4F4" : "transparent";
          },
          pointBorderColor: allZero ? this.grayColor : this.values[this.values.length - 1]?.roas_evol_vs_previous_period > 0 ? this.greenColor : this.redColor,
          pointHoverRadius: 0,
          pointHoverBorderColor: "transparent",
          pointHoverBorderWidth: 0,
          pointHoverWidth: 0,
          pointHoverBackgroundColor: "transparent",
          pointBorderWidth: 2,
          pointHitRadius: 5
        }]
      };
    },
    options() {
      const allZero = this.values.every(v => Number(v.ROAS) === 0);
      const maxValue = allZero ? 1 : Math.max(...this.values.map(v => Math.abs(Number(v?.ROAS))));
      return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: false
          },
          tooltip: {
            enabled: false
          },
          title: {
            display: false
          },
          interaction: {
            mode: "nearest",
            intersect: true
          },
          datalabels: {
            display: false
          },
          dashedLinesToAxis: false
        },
        scales: {
          x: {
            display: false,
            grid: {
              display: false
            }
          },
          y: {
            display: false,
            grid: {
              display: false
            },
            min: 0,
            max: maxValue
          }
        },
        layout: {
          padding: {
            top: 8,
            bottom: 8,
            left: 0,
            right: 0
          }
        },
        animation: {
          onComplete: () => this.getAllPointPosition()
        }
      };
    }
  },
  mounted() {
    const vm = this;
    ChartJS.register({
      id: "lineShadowPlugin",
      beforeDatasetDraw(chart, args, pluginOptions) {
        const {
          ctx,
          chartArea
        } = chart;
        ctx.save();
        ctx.strokeStyle = vm.lineColor;
        ctx.shadowColor = vm.hexToRgba(vm.lineColor, 0.1);
        ctx.shadowBlur = 10;
        ctx.shadowOffsetX = 0;
        ctx.shadowOffsetY = 20;
      },
      afterDatasetDraw(chart, args, pluginOptions) {
        chart.ctx.restore();
      }
    });
    this.$nextTick(() => {
      this.getAllPointPosition();
    });
  },
  methods: {
    hexToRgba(hex, opacity = 1) {
      const bigint = parseInt(hex.replace(/^#/, ""), 16);
      const r = bigint >> 16 & 255;
      const g = bigint >> 8 & 255;
      const b = bigint & 255;
      return `rgba(${r}, ${g}, ${b}, ${opacity})`;
    },
    getAllPointPosition() {
      const roasChartRef = this.$refs[`${this.uid}_roas_chart`];
      if (!roasChartRef) {
        this.pointPositions = [];
        return;
      }
      const chart = roasChartRef.chart;
      if (chart == null) {
        this.pointPositions = [];
        return;
      }
      const meta = chart.getDatasetMeta(0);
      this.pointPositions = meta.data.map((point, index) => {
        const {
          x,
          y
        } = point.getCenterPoint();
        const evol_value = this.values[index]?.roas_evol_vs_previous_period;
        return {
          x,
          y,
          evol_value
        };
      });
    },
    pointCustomStyle(point) {
      return {
        left: point.x + "px",
        top: point.y + "px",
        "--point-hover-bg": point?.evol_value == 0 ? this.grayColor : point?.evol_value > 0 ? this.greenColor : this.redColor
      };
    },
    calculateDateString(data) {
      if (!data?.label) {
        return "-";
      }
      const date1 = new Date(data?.label);
      const date2 = new Date(date1);
      date2.setDate(date2.getDate() + this.periodRange[this.selected_period]);
      const date1_str = `${date1.getDate()} ${date1.toLocaleDateString(navigator.language, {
        month: "short"
      })}`;
      const date2_str = `${date2.getDate()} ${date2.toLocaleDateString(navigator.language, {
        month: "short"
      })} ${date2.getFullYear()}`;
      return `${date1_str} - ${date2_str}`;
    },
    onHovering(index) {
      this.hovering_index = index;
    },
    onLeaving() {
      this.hovering_index = -1;
    }
  }
};